// components/calendar/AgendaCalendarDetailDialog.jsx
import React from 'react';
import {
    Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Tooltip, Typography,
    Accordion, AccordionSummary, AccordionDetails, Fade,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    ExpandMore as ExpandMoreIcon,
    LocationOn as LocationOnIcon,
    OpenInNew as OpenInNewIcon,
    Person as PersonIcon,
    Route as RouteIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { getEstadoConfig, formatDuration } from './agendaCalendar.constants';

const AgendaCalendarDetailDialog = ({ event, onClose }) => {
    const open = !!event;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Fade}
            PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', maxHeight: '90vh' } }}>
            {event && (
                <>
                    <DialogTitle sx={{ pb: 1, background: `linear-gradient(135deg, ${getEstadoConfig(event.resource.manest).bg} 0%, transparent 100%)` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                            <Box>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>Manifiesto</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{event.resource.codigo}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {event.resource.fechaStr} · {event.resource.horaStr}
                                    {event.resource.duracion_minutos && <> · Duración: {formatDuration(event.resource.duracion_minutos)}</>}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    label={getEstadoConfig(event.resource.manest).label}
                                    size="small"
                                    sx={{
                                        fontWeight: 800,
                                        bgcolor: getEstadoConfig(event.resource.manest).bg,
                                        color: getEstadoConfig(event.resource.manest).color,
                                        border: `1px solid ${getEstadoConfig(event.resource.manest).border}`,
                                    }}
                                />
                                <Tooltip title="Abrir manifiesto completo">
                                    <IconButton
                                        component={Link}
                                        color="primary"
                                        to={`/administracion/manifiestos/${event.resource.manid}/pasajeros`}
                                        target="_blank"
                                        sx={{ border: '1px solid', borderColor: 'divider' }}
                                    >
                                        <OpenInNewIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </DialogTitle>

                    <DialogContent dividers sx={{ p: 3, overflowY: 'auto' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>DESCRIPCIÓN DEL SERVICIO</Typography>
                                <Typography variant="body1">{event.resource.maninf || 'Sin descripción disponible'}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>RUTA</Typography>
                                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon fontSize="small" color="primary" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Origen</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.resource.manori || '-'}</Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="h6" color="text.secondary">→</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon fontSize="small" color="error" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Destino</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.resource.mandes || '-'}</Typography>
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Box>

                            {event.resource.paradas?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <RouteIcon fontSize="small" /> PARADAS DEL RECORRIDO ({event.resource.total_paradas || event.resource.paradas.length})
                                    </Typography>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Ver todas las paradas</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={1}>
                                                {[...event.resource.paradas]
                                                    .sort((a, b) => a.parord - b.parord)
                                                    .map((parada, idx) => (
                                                        <Paper key={parada.manparid || idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Chip
                                                                        label={parada.partip}
                                                                        size="small"
                                                                        color={parada.partip === 'ORIGEN' ? 'primary' : parada.partip === 'DESTINO' ? 'error' : 'default'}
                                                                        sx={{ minWidth: 80 }}
                                                                    />
                                                                    <Typography variant="body2">Parada #{parada.parord}</Typography>
                                                                </Box>
                                                                {parada.parhorest && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <AccessTimeIcon sx={{ fontSize: '0.75rem' }} />
                                                                        {format(parseISO(parada.parhorest), 'HH:mm', { locale: es })}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        </Paper>
                                                    ))}
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            )}

                            <Box>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>VEHÍCULO</Typography>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Placa</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{event.resource.vehiculo?.VehMat || 'Sin asignar'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Marca/Modelo</Typography>
                                            <Typography variant="body2">{event.resource.vehiculo?.VehMar || '-'} {event.resource.vehiculo?.VehMod || ''}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Capacidad</Typography>
                                            <Typography variant="body2">{event.resource.vehiculo?.VehCap || '-'} pasajeros</Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>CONDUCTORES ASIGNADOS</Typography>
                                {event.resource.conductores?.length > 0 ? (
                                    <Stack spacing={1}>
                                        {event.resource.conductores.map((c, idx) => (
                                            <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{c.NomUsu}</Typography>
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                        No hay conductores asignados a este manifiesto.
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default AgendaCalendarDetailDialog;